import { Injectable, Injector } from '@angular/core';
import { Subject } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { ApiUrl } from "../shared/models/apiurl.models";
import { SystemApp } from "../constans/SystemConfigurations";
import { UsersService } from "../admin/users/users.service";
import { UserStorageService } from "./UserStorageService";
import { getFreshWsToken } from "./ws-token";

@Injectable({
    providedIn: 'root',
})
export class WebSocketService {
    private socket: WebSocket | null = null;
    private isConnected = false;

    private messagesSubject = new Subject<any>();
    public messages$ = this.messagesSubject.asObservable().pipe(
        shareReplay(1)
    );

    private reconnectInterval = 5000;
    private reconnectTimeout: any = null;

    // Set by switchRoom() (multi-room picker) — appended to the connect URL
    // as `chat_room=<id>` so ChatConsumer.get_or_create_room_with_superuser()
    // resolves to THAT specific room instead of "whichever room this user is
    // in first", which is ambiguous once a user has more than one room (see
    // ChatRoom.get_or_create_for_users in chat_app/models.py).
    private currentRoomId: number | null = null;

    // Token handling - see ws-token.ts for why the socket cannot just reuse
    // whatever token TokenStore holds.
    private resolvingToken = false;
    // Set when a handshake fails before onopen (403). The next attempt then
    // refreshes once even if the token does not look expired. Reset on a
    // successful open, so a failure that is NOT about the token cannot rotate
    // the refresh token every 5 seconds.
    private forceRefresh = false;
    private forcedRefreshUsed = false;
    // Bumped by disconnect(): a token refresh that resolves after logout must
    // not open a socket.
    private generation = 0;

    private typingTimeout!: any;
    private TYPING_DELAY = 3000;

    constructor(
        private apiUrl: ApiUrl,
        private authService: UsersService,
        private userStorage: UserStorageService,
        // Injector, not direct injection: AuthSessionService already reaches
        // this service through its own Injector, so constructor injection in
        // both directions would be a cycle.
        private injector: Injector,
    ) {
        // Reconnect immediately whenever the user logs in
        this.userStorage.loggedIn$.subscribe(() => {
            console.log('[WebSocket] Login detected -> reconnecting with new token');
            this.reconnectNow();
        });
    }

    /** MAIN CONNECTOR - Safe Singleton WebSocket */
    connect(): void {
        // Guarding on isConnected alone let two near-simultaneous callers
        // (the always-mounted global <app-chat-room> and the chatbot's
        // embedded one, which calls reconnectNow() on every open) each
        // create their own WebSocket before the first had even finished
        // connecting - isConnected only flips true in onopen, so nothing
        // stopped a second, third, ... socket from being created in that
        // window. Guard on the in-flight CONNECTING state too.
        if (this.isConnected || this.socket?.readyState === WebSocket.CONNECTING) {
            return;
        }

        if (this.resolvingToken) {
            return; // the pending token lookup opens the socket when it resolves
        }

        if (!this.apiUrl.getJWTTokenKey()) {
            // Not logged in - don't attempt connection, wait for loggedIn$ event
            return;
        }

        const force = this.forceRefresh && !this.forcedRefreshUsed;
        if (force) this.forcedRefreshUsed = true;
        const gen = this.generation;
        this.resolvingToken = true;
        getFreshWsToken(this.injector, force).then(token => {
            this.resolvingToken = false;
            if (gen !== this.generation || !token) return; // logged out meanwhile / no session
            if (this.isConnected || this.socket?.readyState === WebSocket.CONNECTING) return;
            this.openSocket(token);
        });
    }

    private openSocket(token: string): void {

        // POD1-4: the credential is NOT a query parameter any more. A query
        // string is written to nginx access logs, proxy logs and browser
        // history, so a token there is a long-lived credential sitting in
        // plain text in several places at once.
        //
        // A browser cannot set headers on a WebSocket, so the only secure
        // option is the subprotocol field. The backend reads
        // `Sec-WebSocket-Protocol: bearer, <jwt>` and echoes "bearer" back on
        // accept - if it did not echo it, the browser would drop the socket.
        const wsUrl = this.currentRoomId
            ? `${SystemApp.web_socket}chat/?chat_room=${this.currentRoomId}`
            : `${SystemApp.web_socket}chat/`;
        // Deliberately logging the URL only. It used to log the token too.
        console.log('[WebSocket] connecting to:', wsUrl);

        // Captured by value in every handler below (see the `this.socket !==
        // socket` checks) so a handler only ever touches shared state while
        // ITS OWN socket is still the current one.
        //
        // Without that check, an overwritten/orphaned socket (created by an
        // earlier connect() call that got superseded, e.g. by
        // reconnectNow() elsewhere) could still fire onclose LATER - after
        // the CURRENT socket had already connected successfully - and that
        // stale handler would set this.socket = null and isConnected =
        // false for the socket that is actually fine. That is exactly what
        // produced "[WebSocket] Connected" immediately followed by
        // "[WebSocket] Closed -> will reconnect in 5s" in a loop: a healthy
        // connection kept getting torn down by a dead one's belated close
        // event.
        const socket = new WebSocket(wsUrl, ['bearer', token]);
        this.socket = socket;
        let opened = false;

        socket.onopen = () => {
            if (this.socket !== socket) return; // superseded before it opened
            console.log('[WebSocket] Connected');
            opened = true;
            this.isConnected = true;
            this.forceRefresh = false;
            this.forcedRefreshUsed = false;
            if (this.reconnectTimeout) {
                clearTimeout(this.reconnectTimeout);
                this.reconnectTimeout = null;
            }
        };

        socket.onmessage = (event) => {
            if (this.socket !== socket) return;
            try {
                const data = JSON.parse(event.data);
                this.messagesSubject.next(data);
            } catch (err) {
                console.error('[WebSocket] Invalid JSON:', event.data);
            }
        };

        socket.onclose = (event) => {
            if (this.socket !== socket) return; // stale - a newer socket is current
            console.warn('[WebSocket] Closed -> will reconnect in 5s', event.reason);
            if (!opened) {
                // Handshake rejected (403) - most likely the credential.
                this.forceRefresh = true;
            }
            this.isConnected = false;
            this.socket = null;

            if (!this.reconnectTimeout) {
                this.reconnectTimeout = setTimeout(() => {
                    this.reconnectTimeout = null;
                    this.connect();
                }, this.reconnectInterval);
            }
        };

        socket.onerror = (error) => {
            if (this.socket !== socket) return;
            console.error('[WebSocket] Error:', error);
            socket.close();
        };
    }

    /** Reconnect this same socket to a specific room (multi-room picker). */
    switchRoom(roomId: number): void {
        this.currentRoomId = roomId;
        this.reconnectNow();
    }

    /** Force close + reconnect immediately (used after login) */
    reconnectNow(): void {
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }
        this.isConnected = false;
        if (this.socket) {
            this.socket.onclose = null;
            this.socket.close();
            this.socket = null;
        }
        this.connect();
    }

    /** SAFE SEND */
    send(data: any): void {
        if (this.socket?.readyState === WebSocket.OPEN) {
            this.socket.send(JSON.stringify(data));
        } else {
            console.warn('[WebSocket] Cannot send, socket is not ready:', data);
        }
    }

    /** FORCE CLOSE */
    disconnect(): void {
        this.generation++;
        this.resolvingToken = false;
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }
        this.isConnected = false;
        if (this.socket) this.socket.close();
        this.socket = null;
    }

    /** TYPING */
    notifyTyping(): void {
        this.send({ type: 'typing' });

        clearTimeout(this.typingTimeout);
        this.typingTimeout = setTimeout(() => {
            this.notifyStopTyping();
        }, this.TYPING_DELAY);
    }

    notifyStopTyping(): void {
        this.send({ type: 'stop_typing' });
    }
}
